'use client';

/**
 * useSidebarRail Hook
 *
 * Manages the two-layer sidebar state:
 * - Derives active section from the current pathname
 * - Allows user override when clicking a rail icon
 * - Supports collapsing/expanding the 200px panel via toggle button
 * - Persists collapsed state to localStorage
 *
 * Optional `hoverExpand` mode (Issue: Jam-e-Jam-style hover/pin nav):
 * - When enabled, an unpinned panel (`collapsed=true`) auto-shows while
 *   the cursor is over the rail/panel (`hovered=true`) and re-hides when
 *   the cursor leaves. The pin button (a renamed Toggle) latches the
 *   panel open across hovers — semantically `pinned = !collapsed`.
 * - Initial state on first visit defaults to `collapsed=true` so the
 *   user sees just the icon rail until they hover or pin.
 *
 * @see PR #856 — Navigation & Layout Redesign
 */

import type { NavSection } from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const RAIL_WIDTH = 70;
/**
 * Narrow rest-state rail width used in hover-expand mode (Jam-e Jam-style
 * nav). The icon-only resting strip from the design reference is ~48px,
 * which fits 24px icons with a 12px square margin and zero captions.
 * The page content offset uses this when the rail is in expand-on-hover
 * mode so the page recovers ~22px of horizontal real estate vs. the
 * 70px legacy rail.
 */
export const RAIL_REST_NARROW_WIDTH = 56;
/**
 * Width of the rail when its hover-expand mode opens it as a single
 * column with icon + label rows (Jam-e Jam-style nav). The rail
 * positions itself with `position: fixed` and overlays page content
 * during hover, so this width does NOT need to be added to the
 * sidebar offset — content stays at the rest-state width.
 */
export const RAIL_EXPANDED_WIDTH = 240;
export const PANEL_WIDTH = 240;

// ============================================================================
// Types
// ============================================================================

export interface UseSidebarRailOptions {
  /** Current pathname for auto-deriving the active section */
  pathname: string;
  /** Nav sections to match against */
  sections: NavSection[];
  /** localStorage key for persisting collapsed state */
  storageKey?: string;
  /**
   * When true, the panel auto-shows while the cursor is over the
   * rail/panel and auto-hides on cursor leave. The pin button latches
   * the panel open. Default: false (existing toggle behaviour).
   */
  hoverExpand?: boolean;
  /**
   * Initial collapsed state when no localStorage value exists. If
   * omitted, defaults to `true` when `hoverExpand` is on (start with the
   * icon-only rail) and `false` otherwise (existing always-open default).
   */
  defaultCollapsed?: boolean;
}

export interface UseSidebarRailReturn {
  /** Currently active section ID (derived from pathname or user click) */
  activeSection: string | null;
  /** Whether the panel is visible (not collapsed AND a section is active) */
  panelOpen: boolean;
  /** Whether the user has collapsed the panel */
  collapsed: boolean;
  /** Select a section in the panel (and expand if collapsed) */
  openSection: (sectionId: string) => void;
  /** Toggle the panel collapsed/expanded */
  toggleCollapse: () => void;
  /** Total sidebar width (rail only = 70, rail + panel = 270, fullCollapsed = 0) */
  totalWidth: number;
  /** Whether both rail and panel are fully collapsed (Asana-style) */
  fullCollapsed: boolean;
  /** Toggle full collapse of both rail and panel */
  toggleFullCollapse: () => void;
  // ── Hover-expand mode (Issue: Jam-e-Jam nav) ───────────────────────
  /**
   * Transient hover flag. True when the cursor is over the rail or
   * panel. Wired by `PortalLayout`'s onMouseEnter/onMouseLeave so the
   * sidebar stays open while the user moves between rail icons and
   * panel items. Always false when `hoverExpand` is off.
   */
  hovered: boolean;
  /** Set the hover flag. Includes a small leave-delay to suppress flicker. */
  setHovered: (v: boolean) => void;
  /**
   * Section ID currently being PREVIEWED via hover. Takes priority over
   * `userSection` and `pathnameSection` when computing `activeSection`,
   * so hovering a rail icon shows its panel contents without committing.
   * Cleared automatically when the cursor leaves the rail+panel hover
   * region (via the same debounced `setHovered(false)` path). A click
   * promotes the previewed section to `userSection` via `openSection`.
   * Always null when `hoverExpand` is off.
   */
  previewSection: string | null;
  /** Set the preview section. Clears when passed null. No-op when `hoverExpand` is off. */
  setPreviewSection: (sectionId: string | null) => void;
  /**
   * Semantic alias for `!collapsed`. Reads more naturally at call sites
   * in hoverExpand mode where the toggle is a pin/unpin action rather
   * than a collapse/expand one.
   */
  pinned: boolean;
  /** Alias for `toggleCollapse` — flips the pin state. */
  togglePin: () => void;
  // ── Rail expansion (Jam-e-Jam style icon-strip vs labelled rows) ───
  /**
   * Whether the icon rail itself is expanded to its labelled width
   * (240px). Independent from the secondary panel — the rail can be
   * expanded while the panel is collapsed, and vice versa. Toggled
   * by the pin button rendered inside `SidebarRail`. Persisted to
   * localStorage so the user's choice survives reloads.
   */
  railExpanded: boolean;
  /** Toggle the rail's expanded state and persist. */
  toggleRailExpanded: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Find which section matches the current pathname by checking
 * each section's items and their tab paths.
 */
function findSectionByPathname(sections: NavSection[], pathname: string): string | null {
  for (const section of sections) {
    for (const item of section.items) {
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
        return section.id;
      }
      if (item.tabs) {
        for (const tab of item.tabs) {
          if (pathname === tab.path || pathname.startsWith(`${tab.path}/`)) {
            return section.id;
          }
        }
      }
    }
  }
  return null;
}

/**
 * Returns true when the pathname targets a deep-link inside a section —
 * either a tab path or a strict sub-path of an item's root. Matching the
 * section root path itself does NOT count, because that page renders its
 * own primary content (no need to auto-open the secondary panel).
 *
 * Used to decide whether the hoverExpand collapsed-by-default should be
 * overridden on first visit: deep-links need the panel for sibling
 * navigation; root-section visits don't.
 */
function isDeepLinkInSections(sections: NavSection[], pathname: string): boolean {
  for (const section of sections) {
    for (const item of section.items) {
      // Tab path (any exact or sub-path match) — tabs are by definition
      // sibling navigation that the panel surfaces.
      if (item.tabs) {
        for (const tab of item.tabs) {
          if (pathname === tab.path || pathname.startsWith(`${tab.path}/`)) {
            return true;
          }
        }
      }
      // Strict sub-path of the item's root (e.g. `/control-center/preferences`
      // when the item path is `/control-center`). The bare item path itself
      // does not count — `startsWith(`${item.path}/`)` already excludes the
      // equality case because the trailing slash makes the prefix one char
      // longer than `pathname` (PR #1856 follow-up: dropped redundant guard).
      if (pathname.startsWith(`${item.path}/`)) {
        return true;
      }
    }
  }
  return false;
}

// ============================================================================
// Hook
// ============================================================================

interface PersistedState {
  collapsed: boolean | null;
  fullCollapsed: boolean | null;
  railExpanded: boolean | null;
}

/**
 * Reads localStorage. Returns `null` for fields that haven't been
 * persisted yet so the caller can apply per-mode defaults (a stored
 * `false` is meaningful and must not be confused with a fresh visit).
 */
function readPersistedState(storageKey: string): PersistedState {
  if (typeof window === 'undefined') return { collapsed: null, fullCollapsed: null, railExpanded: null };
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        collapsed: typeof parsed.collapsed === 'boolean' ? parsed.collapsed : null,
        fullCollapsed: typeof parsed.fullCollapsed === 'boolean' ? parsed.fullCollapsed : null,
        railExpanded: typeof parsed.railExpanded === 'boolean' ? parsed.railExpanded : null,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { collapsed: null, fullCollapsed: null, railExpanded: null };
}

// Small delay to suppress flicker when the cursor briefly leaves the
// sidebar (e.g. moving from a rail icon to its panel item). 200ms is
// the same window most popover libraries use; long enough to forgive
// a wobble, short enough to feel responsive.
const HOVER_LEAVE_DELAY_MS = 200;

export function useSidebarRail(options: UseSidebarRailOptions): UseSidebarRailReturn {
  const { pathname, sections, storageKey = 'sidebar-rail-state', hoverExpand = false, defaultCollapsed } = options;

  // First-visit default: when no persisted value, prefer the caller's
  // `defaultCollapsed`, falling back to "start collapsed in hoverExpand
  // mode" so the user sees just the icon rail (Jam-e Jam pattern).
  //
  // Deep-link exception: if the initial pathname is a SUB-ROUTE of a
  // section's item (e.g. `/control-center/preferences` under the
  // `/control-center` item) or matches a TAB path, start with the panel
  // OPEN so the user — and assistive tech / Playwright — can immediately
  // navigate to sibling pages in that section. Without this, a user
  // landing on `/control-center/preferences` via URL would only see the
  // icon rail and have no visible way to jump to `/control-center/devices`
  // unless they hovered the (correct) rail icon first. The user can still
  // collapse the panel via the pin button, and the choice persists.
  //
  // Landing on a section ROOT path (e.g. just `/dashboard`) keeps the
  // collapsed-by-default behaviour because the page itself surfaces the
  // content — there's no deep-link disambiguation problem to solve.

  // Collapsed + fullCollapsed states (persisted to localStorage)
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const persisted = readPersistedState(storageKey).collapsed;
    if (persisted !== null) return persisted;
    if (defaultCollapsed !== undefined) return defaultCollapsed;
    if (!hoverExpand) return false;
    // hoverExpand fresh-visit default: collapsed unless the initial route
    // is a deep-link inside a section. Deep-link = matches a tab path or
    // is a strict sub-path of an item's root path.
    return !isDeepLinkInSections(sections, pathname);
  });
  const [fullCollapsed, setFullCollapsed] = useState<boolean>(
    () => readPersistedState(storageKey).fullCollapsed ?? false,
  );
  // Rail expansion (icon-only strip vs. icon+label rows). Defaults to
  // collapsed in hover-expand mode so the user sees the narrow strip
  // first and can pin it open via the rail's pin button.
  const [railExpanded, setRailExpanded] = useState<boolean>(() => readPersistedState(storageKey).railExpanded ?? false);

  // Transient hover flag — never persisted. Only meaningful when
  // hoverExpand is true; otherwise it's pinned at false.
  const [hovered, setHoveredState] = useState<boolean>(false);
  const hoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Transient preview-on-hover section. Cleared when the cursor leaves
  // the rail+panel hover region. Like `hovered`, never persisted and
  // forced null when hoverExpand is off.
  const [previewSection, setPreviewSectionState] = useState<string | null>(null);

  // User-selected section override (when clicking a rail icon for a different section)
  const [userSection, setUserSection] = useState<string | null>(null);

  // Section derived from current pathname
  const pathnameSection = useMemo(() => findSectionByPathname(sections, pathname), [sections, pathname]);

  // Active section: hover-preview > click-override > pathname-derived.
  // The preview tier exists so that hovering a rail icon shows that
  // section's panel without committing — moving the mouse off the rail
  // and panel reverts to the active page's section.
  const activeSection = previewSection ?? userSection ?? pathnameSection;

  // When pathname moves to a different section, drop the user-section
  // override SO the panel re-tracks the route — UNLESS the user has
  // pinned the panel open. A pinned panel means the user explicitly
  // chose to keep this section visible regardless of where they
  // navigate next (e.g. pin Relations, then drill into a Borrowing
  // Entity detail page that's filed under Control Center — the
  // Relations panel should stay visible). The unpinned state is the
  // legacy "panel follows the route" behavior.
  const prevPathnameSection = useRef(pathnameSection);
  useEffect(() => {
    if (pathnameSection !== prevPathnameSection.current) {
      prevPathnameSection.current = pathnameSection;
      // `collapsed === true` ⇔ unpinned. Pinned panels survive nav.
      if (collapsed) {
        setUserSection(null);
      }
    }
  }, [pathnameSection, collapsed]);

  // Panel visibility — gated by an active section, the full-collapse flag,
  // and either the user's pin choice or (in hoverExpand mode) a transient
  // hover. In hoverExpand mode `collapsed` reads as "not pinned"; the
  // hover signal floats the panel open without persisting anything.
  const panelOpen = !fullCollapsed && activeSection !== null && (hoverExpand ? !collapsed || hovered : !collapsed);

  // Wrap the raw setter in a debounced leaver so a brief mouse-out
  // doesn't auto-collapse mid-interaction. Enter is immediate; only
  // leave is delayed. No-op when `hoverExpand` is off so the docstring's
  // "hovered: always false" promise holds and we don't schedule timers
  // that nothing reads.
  //
  // Leaving the rail+panel also clears `previewSection` so the panel
  // reverts to whatever the active page's section was — the preview
  // tier is transient by design.
  const setHovered = useCallback(
    (next: boolean) => {
      if (!hoverExpand) return;
      if (next) {
        if (hoverLeaveTimerRef.current) {
          clearTimeout(hoverLeaveTimerRef.current);
          hoverLeaveTimerRef.current = null;
        }
        setHoveredState(true);
      } else {
        if (hoverLeaveTimerRef.current) clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = setTimeout(() => {
          setHoveredState(false);
          setPreviewSectionState(null);
          hoverLeaveTimerRef.current = null;
        }, HOVER_LEAVE_DELAY_MS);
      }
    },
    [hoverExpand],
  );

  const setPreviewSection = useCallback(
    (sectionId: string | null) => {
      if (!hoverExpand) return;
      // Clear any pending leave-timer so a hover on a sibling icon doesn't
      // race with the previous icon's leave fire.
      if (sectionId !== null && hoverLeaveTimerRef.current) {
        clearTimeout(hoverLeaveTimerRef.current);
        hoverLeaveTimerRef.current = null;
      }
      setPreviewSectionState(sectionId);
      // Keyboard parity: tabbing through rail icons fires onFocus → this
      // → setPreviewSection(id) with no corresponding mouseenter. Without
      // also flipping `hovered` to true, the panel opens but the next
      // mouse-out (or focus-out) closes it without the 200ms debounce
      // grace window. Mirror the mouseenter side-effect so keyboard
      // users get the same dwell behavior as mouse users.
      if (sectionId !== null) {
        setHoveredState(true);
      }
    },
    [hoverExpand],
  );

  // Cancel any pending leave timer when unmounting so we don't fire a
  // setState on a torn-down component.
  useEffect(
    () => () => {
      if (hoverLeaveTimerRef.current) clearTimeout(hoverLeaveTimerRef.current);
    },
    [],
  );

  const persistState = useCallback(
    (collapsedVal: boolean, fullCollapsedVal: boolean, railExpandedVal?: boolean) => {
      if (typeof window === 'undefined') return;
      try {
        const prevRail = readPersistedState(storageKey).railExpanded ?? false;
        localStorage.setItem(
          storageKey,
          JSON.stringify({
            collapsed: collapsedVal,
            fullCollapsed: fullCollapsedVal,
            railExpanded: railExpandedVal ?? prevRail,
          }),
        );
      } catch {
        // Ignore storage errors
      }
    },
    [storageKey],
  );

  const openSection = useCallback(
    (sectionId: string) => {
      setUserSection(sectionId);
      // Clicking commits a section — drop the transient preview so the
      // panel doesn't double-react when the same icon was being hovered
      // immediately before the click.
      setPreviewSectionState(null);
      // Compute final values first to avoid stale closure when both are true
      const nextCollapsed = false;
      const nextFullCollapsed = false;
      if (collapsed) setCollapsed(nextCollapsed);
      if (fullCollapsed) setFullCollapsed(nextFullCollapsed);
      if (collapsed || fullCollapsed) {
        persistState(nextCollapsed, nextFullCollapsed);
      }
    },
    [collapsed, fullCollapsed, persistState],
  );

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      // Use functional ref to fullCollapsed to avoid stale closure
      setFullCollapsed((fc) => {
        persistState(next, fc);
        return fc;
      });
      return next;
    });
  }, [persistState]);

  const toggleFullCollapse = useCallback(() => {
    setFullCollapsed((prev) => {
      const next = !prev;
      // Use functional ref to collapsed to avoid stale closure
      setCollapsed((c) => {
        persistState(c, next);
        return c;
      });
      return next;
    });
  }, [persistState]);

  const toggleRailExpanded = useCallback(() => {
    setRailExpanded((prev) => {
      const next = !prev;
      setCollapsed((c) => {
        setFullCollapsed((fc) => {
          persistState(c, fc, next);
          return fc;
        });
        return c;
      });
      return next;
    });
  }, [persistState]);

  const totalWidth = useMemo(
    () => (fullCollapsed ? 0 : panelOpen ? RAIL_WIDTH + PANEL_WIDTH : RAIL_WIDTH),
    [fullCollapsed, panelOpen],
  );

  return {
    activeSection,
    panelOpen,
    collapsed,
    openSection,
    toggleCollapse,
    totalWidth,
    fullCollapsed,
    toggleFullCollapse,
    // Force false when hoverExpand is off so the public contract matches
    // the docstring: consumers can rely on `hovered === false` in legacy
    // mode without needing to know the internal state machine.
    hovered: hoverExpand ? hovered : false,
    setHovered,
    previewSection: hoverExpand ? previewSection : null,
    setPreviewSection,
    pinned: !collapsed,
    togglePin: toggleCollapse,
    railExpanded,
    toggleRailExpanded,
  };
}
